import { useQuery } from '@tanstack/react-query';
import { System } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/system_pb';
import React, { useCallback, useMemo } from 'react';
import { useLoaderData, useParams } from 'react-router';
import { getSystemQuery } from '~/shared/api/system.queries';
import { useOpenIssuesQuery } from '~/shared/hooks/issues-hooks';
import { useRequiredSimulation } from '~/shared/hooks/useSimulation';
import { SystemModel } from '~/shared/models/system/SystemModel';
import { assertIsDefined } from '~/types/assert-type';

export const useGetSystemResponse = () => {
  const initialData = useLoaderData() as {
    system: System;
  };
  const { systemId } = useParams<{ systemId: string }>();

  assertIsDefined(systemId);

  return useQuery({
    ...getSystemQuery(systemId),
    initialData: initialData?.system,
  });
};

export const useRequiredSystemRegular = (): SystemModel => {
  const { data: system } = useGetSystemResponse();

  const issues = useOpenIssuesQuery();

  const systemModel = useMemo(
    () => new SystemModel({ system, issuesResponse: issues }),
    [system, issues],
  );

  return systemModel;
};

export const useRequiredSystemInSimulation = (): SystemModel => {
  const simulation = useRequiredSimulation();

  assertIsDefined(simulation.system);

  return simulation.system;
};

const RequiredSystemContext = React.createContext<() => SystemModel>(
  useRequiredSystemRegular,
);

export function RequiredSystemInSimulation({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <RequiredSystemContext.Provider value={useRequiredSystemInSimulation}>
      {children}
    </RequiredSystemContext.Provider>
  );
}

export function RequiredSystemInSimulationCard({
  children,
  system,
}: {
  children: React.ReactNode;
  system: SystemModel;
}) {
  const getSystem = useCallback(() => system, [system]);

  return (
    <RequiredSystemContext.Provider value={getSystem}>
      {children}
    </RequiredSystemContext.Provider>
  );
}

export const useRequiredSystem = (): SystemModel => {
  const useGetSystem = React.useContext(RequiredSystemContext);
  return useGetSystem();
};
